import React, { useRef } from 'react';

import infoGraySvg from '@assets/img/icons/info-gray.svg';
import infoSvg from '@assets/img/icons/info.svg';
import { bookingInquiryStrings, bookingInquiryTradeConditionStrings } from '@data/bookingInquiry';
import { invoiceDetailsMetaList } from '@data/invoice';
import { moneyStrings } from '@data/money';
import { priceSimulatorStrings } from '@data/priceSimulator';
import { CargoSummary, TradeCondition } from '@models/BookingInquiry';
import { BaseInvoiceDetail } from '@models/Invoice';
import { InvoiceHelper } from '@models/next/InvoiceHelper';
import { SimulatorInvoiceDetail, SimulatorResult } from '@models/RoutePlan';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import { sumBy } from 'lodash-es';
import useHoverDirty from 'react-use/lib/useHoverDirty';

const strings = createStrings({
  ja: {
    shipperCost: `${bookingInquiryStrings.shipper}側負担費用`,
    consigneeCost: `${bookingInquiryStrings.consignee}側負担費用`,
    total: '合計',
    baseKgHintString:
      'D2Dの最小重量は280KGSです。280KGS未満の数字が入力された場合には自動で最小重量が表示されます。',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  priceSettlementContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    columnGap: 40,
  },
  mainKeyValueContainer: {
    ...common.textGray700,
    ...common.middleText,
    ...common.fontWeight600,
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    '.confirmation &': {
      ...common.textPrimaryImportant,
    },
  },
  mainKeyValuesContainer: {
    ...common.flexColumn,
    rowGap: '0.5rem',
    paddingBottom: '0.75rem',
    borderBottom: `1px dashed ${theme.palette.grey[400]}`,
  },
  keyValuesContainer: {
    ...common.flexColumn,
    ...common.textGray500,
    ...common.smallerText,
    paddingBottom: '24px',
    rowGap: '2px',
    borderBottom: `1px dashed ${theme.palette.grey[400]}`,
  },
  keyValueContainer: {
    ...common.flexColumn,
    display: 'grid',
    gridTemplateColumns: '1fr auto',
  },
  totalKeyValueContainer: {
    ...common.flexColumn,
    ...common.normalText,
    ...common.fontWeight600,
    ...common.textPrimaryDark,
    marginTop: 8,
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    '.confirmation &': {
      ...common.textPrimaryImportant,
    },
  },
  explanationContainer: {
    ...common.smallerText,
    ...common.textGray700,
    ...common.selfStart,
    rowGap: 30,
    '& ul': {
      paddingInlineStart: '1.2em',
    },
    '& li': {
      listStyleType: 'none',
      position: 'relative',
    },
    '& li::before': {
      content: '"・"',
      position: 'absolute',
      left: '-1.25em',
    },
  },
  invoiceDetailsContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    columnGap: '1.5rem',
    marginTop: 8,
  },
  invoiceDetailsPayer: {
    ...common.textPrimary,
    ...common.smallerText,
    ...common.fontWeight600,
    '$disabled &': {
      ...common.textGray700Important,
    },
  },
  totalPriceContainer: {
    ...common.flexInline,
    ...common.justifyBetween,
    ...common.textPrimary,
    ...common.smallerText,
    ...common.fontWeight600,
    marginTop: 8,
    '$disabled &': {
      ...common.textGray700Important,
    },
  },
  baseKgHintContainer: {
    ...common.baseButton,
    display: 'inline',
    position: 'relative',
    marginLeft: '0.5rem',
    top: 1,
  },
  hintContainer: {
    ...common.exSmallerText,
    ...common.textGray700,
    padding: 9,
    lineHeight: 1.75,
    backgroundColor: '#F2F7F8',
    position: 'absolute',
    left: 'calc(100% + 5px)',
    bottom: '50%',
    width: 250,
    zIndex: 100,
    border: `1px solid ${theme.palette.primary.main}`,
  },
  slash: {
    color: theme.palette.grey[300],
  },
  disabled: {},
}));

const compareInvoiceDetailFn = (a: BaseInvoiceDetail, b: BaseInvoiceDetail) => {
  const orderA = invoiceDetailsMetaList.findIndex((c) => c.name === a.invoice_name);
  const orderB = invoiceDetailsMetaList.findIndex((c) => c.name === b.invoice_name);
  return orderA - orderB;
};

const sortByInvoiceOrder = (invoiceDetails: SimulatorInvoiceDetail[]) => {
  return [...invoiceDetails].sort(compareInvoiceDetailFn);
};

const BaseKgHint: React.FC<{ disabled: boolean }> = ({ disabled }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const infoRef = useRef<HTMLImageElement>(null);

  const isHoveringInfo = useHoverDirty(infoRef);

  return (
    <div className={classes.baseKgHintContainer}>
      <img alt="Info" src={disabled ? infoGraySvg : infoSvg} ref={infoRef} />
      {isHoveringInfo && <div className={classes.hintContainer}>{strings.baseKgHintString}</div>}
    </div>
  );
};

export const PriceSettlement2: React.FC<{
  simulator: SimulatorResult;
  tradeCondtion: TradeCondition;
  cargoSummary?: CargoSummary;
  variant?: 'confirmation';
  disabled?: boolean;
}> = ({ simulator, tradeCondtion, variant, cargoSummary, disabled = false }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const invoiceDetails = {
    cn: simulator.invoice_details.filter((item) => item.payer_mark === 'cn'),
    jp: simulator.invoice_details.filter((item) => item.payer_mark === 'jp'),
  };

  const total = {
    cn: sumBy(
      invoiceDetails.cn,
      (c) =>
        InvoiceHelper.getInvoiceDetailsComputed(c).totalPriceForCurrency(
          c.exchange_base_rate,
          'JPY',
        ) ?? 0,
    ),
    jp: sumBy(
      invoiceDetails.jp,
      (c) =>
        InvoiceHelper.getInvoiceDetailsComputed(c).totalPriceForCurrency(
          c.exchange_base_rate,
          'JPY',
        ) ?? 0,
    ),
  };

  const renderMainKeyValue = (key: React.ReactNode, value: React.ReactNode) => (
    <div className={classes.mainKeyValueContainer}>
      <div>{key}</div>
      <div>{value}</div>
    </div>
  );

  const renderKeyValue = (id: number, key: React.ReactNode, value: React.ReactNode) => (
    <div key={id} className={classes.keyValueContainer}>
      <div>{key}</div>
      <div>{value}</div>
    </div>
  );

  const renderPrices = () => (
    <div style={{ minWidth: 0 }}>
      <div className={classes.mainKeyValuesContainer}>
        {cargoSummary &&
          renderMainKeyValue(
            <>{bookingInquiryStrings.totalCargos}</>,
            <>
              {cargoSummary.unit || '-'} Units
              <span className={classes.slash}> / </span>
              {cargoSummary.kg?.toFixed(3) || '-'} KGS
              <span className={classes.slash}> / </span>
              {cargoSummary.cbm?.toFixed(3) || '-'} CBM
            </>,
          )}
        {renderMainKeyValue(
          <>
            {priceSimulatorStrings.base_kg}
            <BaseKgHint disabled={disabled} />
          </>,
          <>
            {simulator.target_kg} KG
            {simulator.target_kg > 1 ? 'S' : ''}
          </>,
        )}

        {renderMainKeyValue(
          bookingInquiryStrings.bestDeliveryDate,
          <>
            {simulator.delivery_days} day{simulator.delivery_days > 1 ? 's' : ''}
          </>,
        )}

        {renderMainKeyValue(
          bookingInquiryStrings.tradeCondition,
          <>{bookingInquiryTradeConditionStrings[tradeCondtion]}</>,
        )}
      </div>

      <div className={classes.invoiceDetailsContainer}>
        <div className={classes.invoiceDetailsPayer}>{strings.shipperCost}</div>
        <div className={classes.invoiceDetailsPayer}>{strings.consigneeCost}</div>
        <div className={classes.keyValuesContainer}>
          {invoiceDetails.cn.length === 0 && formStrings.none}

          {sortByInvoiceOrder(invoiceDetails.cn).map((item, i) =>
            renderKeyValue(
              i,
              item.invoice_name,
              moneyStrings.formatYen(
                InvoiceHelper.getInvoiceDetailsComputed(item).totalPriceForCurrency(
                  item.exchange_base_rate,
                  'JPY',
                ) ?? 0,
              ),
            ),
          )}
        </div>

        <div className={classes.keyValuesContainer}>
          {invoiceDetails.jp.length === 0 && formStrings.none}

          {sortByInvoiceOrder(invoiceDetails.jp).map((item, i) =>
            renderKeyValue(
              i,
              item.invoice_name,
              moneyStrings.formatYen(
                InvoiceHelper.getInvoiceDetailsComputed(item).totalPriceForCurrency(
                  item.exchange_base_rate,
                  'JPY',
                ) ?? 0,
              ),
            ),
          )}
        </div>

        <div className={classes.totalPriceContainer}>
          <span>{strings.total}</span>
          <span>{moneyStrings.formatYen(total.cn)}</span>
        </div>

        <div className={classes.totalPriceContainer}>
          <span>{strings.total}</span>
          <span>{moneyStrings.formatYen(total.jp)}</span>
        </div>
      </div>

      <div style={{ height: 8 }} />

      <div className={clsx(classes.textError, classes.smallerText)}>
        {priceSimulatorStrings.expectedCostHint}
      </div>
    </div>
  );

  const renderExplanation = () => (
    <div className={classes.explanationContainer} style={{ minWidth: 0 }}>
      <div>
        {priceSimulatorStrings.excludedCostTitle}
        <ul>
          <li>{priceSimulatorStrings.tax}</li>
          <li>{priceSimulatorStrings.clearance}</li>
          <li>{priceSimulatorStrings.insurance}</li>
        </ul>
      </div>
      <div style={{ height: 18 }} />
      <div>{priceSimulatorStrings.reference()}</div>
    </div>
  );

  return (
    <div className={clsx(classes.priceSettlementContainer, variant, disabled && classes.disabled)}>
      {renderPrices()}
      {renderExplanation()}
    </div>
  );
};
